import { Listbox as BaseListbox } from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { ButtonSpinner, H5, Icon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { useFloatingLayer } from "@sellar-io/components";

const ListboxRoot = ({
  value,
  onChange,
  children,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  disabled?: boolean;
}) => {
  return (
    <BaseListbox onChange={onChange} value={value} disabled={disabled}>
      {children}
    </BaseListbox>
  );
};

const Button = ({
  icon,
  children,
  compact = false,
  showChildren = false,
  className,
  containerClassName,
  onClick,
  loading,
}: {
  icon?: IconType;
  children: string | string[];
  compact?: boolean;
  className?: string;
  containerClassName?: string;
  showChildren?: boolean;
  onClick?: () => void;
  loading?: boolean;
}) => {
  return (
    <BaseListbox.Button className={containerClassName}>
      <div
        className={twMerge(
          "flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-solid bg-gray-100 p-3 font-semibold hover:bg-gray-200",
          className,
        )}
        onClick={() => onClick?.()}
      >
        {loading ? (
          <ButtonSpinner className={"text-black"} />
        ) : (
          <>
            {icon && <Icon icon={icon} />}
            {(!compact || showChildren) && children}
            {!compact && <Icon icon={"CHEVRON_DOWN"} />}
          </>
        )}
      </div>
    </BaseListbox.Button>
  );
};

const InputButton = ({
  icon,
  className,
  children,
  compact,
  showChildren,
  onClick,
  loading,
}: {
  className?: string;
  icon?: IconType;
  children: string;
  compact?: boolean;
  showChildren?: boolean;
  onClick?: () => void;
  loading?: boolean;
}) => {
  return (
    <Button
      className={twMerge(
        "-m-3 ml-0 rounded-l-none border-0 border-l border-solid bg-white whitespace-nowrap hover:bg-gray-100",
        className,
      )}
      compact={compact}
      showChildren={showChildren}
      icon={icon}
      onClick={() => onClick?.()}
      loading={loading}
    >
      {children}
    </Button>
  );
};

const Options = ({
  children,
  anchor = "bottom start",
}: {
  children: ReactNode;
  anchor?: "bottom start" | "bottom end";
}) => {
  const { popover } = useFloatingLayer();

  return (
    <BaseListbox.Options
      anchor={anchor}
      className={`${popover} max-h-[400px] min-w-[var(--button-width)] overflow-auto overscroll-contain rounded-lg border border-solid bg-white`}
    >
      {children}
    </BaseListbox.Options>
  );
};

const Header = ({ children }: { children: string }) => {
  return (
    <div className={"border-b border-solid bg-gray-100 px-9 py-2 uppercase"}>
      <H5>{children}</H5>
    </div>
  );
};

const OptionListElement = ({
  active,
  children,
  selected,
}: {
  active: boolean;
  selected: boolean;
  children: ReactNode;
}) => {
  return (
    <li
      className={twMerge(
        "flex cursor-pointer items-center gap-2 px-9 py-4 font-semibold",
        active && "bg-gray-100",
        selected && "pl-2",
      )}
    >
      {children}
    </li>
  );
};

const MultipleSelectOption = ({
  value,
  children,
  isSelected,
}: {
  value: string | number;
  children: ReactNode;
  isSelected?: boolean;
}) => {
  // Hacky way of doing this - need to look into standardised way of doing this with headless ui
  return (
    <BaseListbox.Option value={value} as={Fragment}>
      {({ active, selected }) => (
        <li
          className={twMerge(
            "flex cursor-pointer items-center gap-2 px-9 py-4 font-semibold",
            active && "bg-gray-100",
            selected && "pl-2",
          )}
        >
          {isSelected ? (
            <Icon icon={"CHECKBOX_MARKED"} />
          ) : (
            <Icon icon={"CHECKBOX_BLANK_OUTLINE"} />
          )}
          {children}
        </li>
      )}
    </BaseListbox.Option>
  );
};
const Option = ({
  value,
  children,
}: {
  value: string | number;
  children: ReactNode;
}) => {
  return (
    <BaseListbox.Option value={value} as={Fragment}>
      {({ active }) => (
        <li
          className={twMerge(
            "flex cursor-pointer items-center gap-2 px-4 py-4 font-semibold",
            active && "bg-gray-100",
          )}
        >
          {children}
        </li>
      )}
    </BaseListbox.Option>
  );
};

export {
  ListboxRoot as Listbox,
  Button as ListboxButton,
  InputButton as ListboxInputButton,
  Options as ListboxOptions,
  Option as ListboxOption,
  OptionListElement as ListboxOptionListElement,
  MultipleSelectOption as ListboxMultipleSelectOption,
  Header as ListboxHeader,
};
